import { forwardRef, type ElementType } from 'react';
import { TableContainer, type TableContainerProps, type PaperProps } from '@mui/material';

/**
 * A TableContainer that a keyboard user can actually scroll.
 *
 * REV-507: MUI's TableContainer sets `overflow-x: auto` but leaves the element
 * unfocusable (`tabIndex -1`). Once a table is wider than its container, the
 * off-screen columns are reachable by mouse wheel or trackpad but not by
 * keyboard — there is no way to scroll a region you cannot focus. That is a
 * WCAG 2.1.1 (Keyboard) failure, and it is the real defect behind the
 * walkthrough note that the findings table "hides columns" at narrow widths.
 * Measured: no column is ever `display: none`; the columns squash, then the
 * container scrolls, and keyboard users simply cannot reach what scrolled off.
 *
 * `tabIndex={0}` is the fix that matters. `label` additionally promotes the
 * scroll area to a named landmark, which is the enhanced form — pass it when a
 * meaningful name exists (usually the same string as the table's own
 * `aria-label`). Do NOT set `role="region"` without a name: an unnamed region
 * is worse than none, and our own engine flags it.
 */
export interface ScrollableTableContainerProps extends TableContainerProps {
  /** Accessible name for the scrollable region. Omit if no meaningful name exists. */
  label?: string;
  /**
   * `component` and `variant` are re-declared because MUI's polymorphic typing
   * does not survive `extends TableContainerProps`. Most call sites render as
   * `component={Paper} variant="outlined"`, and `variant` belongs to Paper, not
   * to TableContainer.
   */
  component?: ElementType;
  variant?: PaperProps['variant'];
}

const ScrollableTableContainer = forwardRef<HTMLDivElement, ScrollableTableContainerProps>(
  function ScrollableTableContainer({ label, children, ...rest }, ref) {
    return (
      <TableContainer
        ref={ref}
        tabIndex={0}
        {...(label ? { role: 'region', 'aria-label': label } : {})}
        {...rest}
      >
        {children}
      </TableContainer>
    );
  }
);

export default ScrollableTableContainer;
